import * as React from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
  GridToolbar,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect, useState } from 'react';
import {
  IntlProvider,
  LocalizationProvider,
  loadMessages,
  load,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { getLocalDateTime, dateFormat } from '../../utils/utils';
import { LoadingPanel } from '../LoadingPanel';
import { FetchError } from '../../Interfaces/FetchError';

import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { initialTicketsGridDataState } from '../../store/UserSettings/UserSettingsState';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { saveTicketsGridState } from '../../store/UserSettings/UserSettingsActions';
import { TicketRecord } from '../../Interfaces/Tickets/TicketRecord';
import {
  clearCloseTicketNotificationSuccessMessage,
  fetchTickets,
  resetTicketsError,
  sendCloseTicket,
} from '../../store/Tickets/TicketsActions';
import {
  IncidentStatusCell,
  IncidentStatusFilterCell,
} from './IncidentStatusCell';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { TicketCloseInfo } from '../../Interfaces/Tickets/TicketCloseInfo';
import SplitButtonTicketActions from '../SplitButton/SplitButtonTicketActions';
import { getCommandInfo } from '../Commands/GetCommandInfo';
import {
  clearCommandNotificationSuccessMessage,
  sendDeviceCommandFromTicket,
} from '../../store/Devices/AssetStatesActions';
import StringResourceManager, { GetString } from '../StringResourceManager';
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
import esMessages from '../../utils/gridMessageKey.json';
import { CSVLink } from 'react-csv';
import { TickeStatusCSV } from '../../Interfaces/Tickets/TicketStatusCSV';
loadMessages(esMessages, 'fr-CA');
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones
);
import TicketFilter from './TicketFilter';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import { CommandInfo } from '../../Interfaces/Commands/CommandInfo';
import {
  ColumnMenu,
  CustomCell,
  HeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarButton: {
      height: 40,
    },
  })
);

const CreatedOnDateTimeCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.CreatedOn && dataItem.CreatedOn != '0001-01-01T00:00:00') {
    return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
  }
  return <td></td>;
};

const ETADateTimeCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.EngineerEta && dataItem.EngineerEta != '0001-01-01T00:00:00') {
    return <td>{getLocalDateTime(dataItem.EngineerEta)}</td>;
  }
  return <td></td>;
};

const processWithGroups = (data: any[], dataState: State): DataResult => {
  if (dataState === undefined) {
    dataState = initialTicketsGridDataState;
  }

  const newDataState = process(data, dataState);

  setGroupIds({ data: newDataState.data, group: dataState.group });
  return newDataState;
};

interface Props {
  setLastRefreshQuery: (lastRefreshDate: Date) => void;
}

const TicketsGrid: React.FC<Props> = ({
  setLastRefreshQuery,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const status = GetString('STATUS');

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const [uniqueKeyOnRefresh, setUniqueKeyOnRefresh] = React.useState<Date>(
    new Date()
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) => state.ticketsReducer.isFetchingTickets
  );

  const tickets: TicketRecord[] = useSelector(
    (state: ApplicationState) => state.ticketsReducer.tickets
  );

  useEffect(() => {
    setResultState(processWithGroups(tickets, dataState));
  }, [tickets]);

  const dataState: State = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.ticketsDataState
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.ticketsReducer.fetchError
  );

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(tickets, dataState)
  );

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(tickets, event.dataState);

    saveTicketsGridState(dispatch, event.dataState);
    setResultState(newDataState);
  };

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [commandInfo, setCommandInfo] = React.useState<
    CommandInfo | undefined
  >();

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  const [error, displayError] = React.useState(false);

  const clearFetchError = () => {
    displayError(false);
    resetTicketsError(dispatch);
  };

  const commandNotificationSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.notificationSuccessMessage
  );

  useEffect(() => {
    let timeoutId: any;
    if (commandNotificationSuccessMessage) {
      timeoutId = setTimeout(() => {
        resetCommandNotificationSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandNotificationSuccessMessage]);

  const resetCommandNotificationSuccessMessage = () => {
    clearCommandNotificationSuccessMessage(dispatch, commandInfo);
  };

  const closeTicketNotificationSuccessMessage: string = useSelector(
    (state: ApplicationState) => state.ticketsReducer.notificationSuccessMessage
  );

  const commandNotificationRebootSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.notificationRebootSuccessMessage
  );

  useEffect(() => {
    let timeoutId: any;
    if (closeTicketNotificationSuccessMessage) {
      timeoutId = setTimeout(() => {
        resetCloseTicketNotificationSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [closeTicketNotificationSuccessMessage]);

  const resetCloseTicketNotificationSuccessMessage = () => {
    clearCloseTicketNotificationSuccessMessage(dispatch);
  };

  const onFetchTickets = () => {
    let newDataState: State = {
      skip: 0,
      take: dataState.take,
      sort: dataState.sort,
      group: dataState.group,
    };
    setUniqueKeyOnRefresh(new Date());
    saveTicketsGridState(dispatch, newDataState);
    processFetchTickets();
  };

  const [verticalPanes, setVerticalPanes] = React.useState<Array<any>>([
    {},
    {},
  ]);

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  const closeTicket = (
    ticket: TicketRecord,
    ticketCloseInfo: TicketCloseInfo
  ) => {
    sendCloseTicket(dispatch, ticket.IncidentID, ticketCloseInfo);
  };

  const handleButtonClick = (ticket: TicketRecord, index: number) => {
    if (index === 0) {
      // TODO: Show modal for the user to enter notes/details?
      let ticketCloseInfo: TicketCloseInfo = {
        Source: ApiEnums.Source.Support,
        Notes: '',
      };
      closeTicket(ticket, ticketCloseInfo);
    } else if (index == 1) {
      // TODO: Show modal for the user to enter notes/details?
      let commandInfo = getCommandInfo(ticket.Category);
      sendDeviceCommandFromTicket(dispatch, ticket, commandInfo);

      setCommandInfo(commandInfo);
    } else if (index == 2) {
      // TODO: Show modal for the user to enter notes/details?
      let commandInfo: CommandInfo = { Command: 'Reboot', Parameters: '' };
      sendDeviceCommandFromTicket(dispatch, ticket, commandInfo);

      setCommandInfo(commandInfo);
    }
  };

  const TicketActionsCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem?.IncidentID) {
      return (
        <td>
          <SplitButtonTicketActions
            ticket={dataItem}
            handleButtonClick={handleButtonClick}
          />
        </td>
      );
    }
    return null;
  };
  useEffect(() => {
    processFetchTickets();
  }, []);

  const processFetchTickets = () => {
    fetchTickets(dispatch, true, false);
    setLastRefreshQuery(new Date());
  };

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [csvData, setCSVData] = React.useState<TickeStatusCSV[]>([]);

  const getCSVFileName = () => {
    return `Tickets_Status_export_${dateFormat(currentLocale.language)}`;
  };

  const convertTicketStatus = (value: ApiEnums.IncidentStatus) => {
    switch (value) {
      case ApiEnums.IncidentStatus.Unknown:
        return 'Unkown';
      case ApiEnums.IncidentStatus.Open:
        return 'Open';
      case ApiEnums.IncidentStatus.Resolved:
        return 'Resolved';
      case ApiEnums.IncidentStatus.InProgress:
        return 'InProgress';
      case ApiEnums.IncidentStatus.Closed:
        return 'Closed';
      default:
        return;
    }
  };

  const processExportCSV = () => {
    let result: TickeStatusCSV[] = [];

    if (Array.isArray(tickets)) {
      tickets.forEach(ticket => {
        let data: TickeStatusCSV = {
          Division: ticket.Division,
          Store: ticket.SiteNumber,
          Cluster: ticket.NodeGroup,
          Lane: ticket.LaneNumber,
          Category: ticket.Category,
          Failure: ticket.FailureCode,
          CreatedOn: getLocalDateTime(ticket.CreatedOn),
          Status: convertTicketStatus(ticket.Status),
          ETA: ticket.EngineerEta,
        };
        result.push(data);
      });
    }
    setCSVData(result);
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const etaDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="EngineerEta"
      />
    );
  };

  useEffect(() => {
    let timeoutId: any;
    if (commandNotificationRebootSuccessMessage) {
      timeoutId = setTimeout(() => {
        resetCommandNotificationSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandNotificationRebootSuccessMessage]);

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <CSVLink
            data={csvData}
            ref={csvLinkRef}
            filename={getCSVFileName() + '.csv'}
          />
          {loading && LoadingPanel}
          <TicketFilter />
          <Grid
            key={`${uniqueKeyOnRefresh}`}
            style={{ height: '73vh' }}
            data={resultState}
            {...dataState}
            onDataStateChange={dataStateChange}
            resizable={true}
            sortable={true}
            filterable={true}
            pageable={{
              buttonCount: 4,
              pageSizes: [100, 500, 1000, 5000],
            }}
            groupable={true}
            reorderable={true}
            fixedScroll={false}
            cells={{
              headerCell: HeaderCustomCell,
              data: CustomCell,
            }}
          >
            <GridToolbar>
              <Button
                onClick={onFetchTickets}
                type="submit"
                className={classes.toolbarButton}
              >
                {GetString('RUN_QUERY')}
              </Button>
              <Button
                onClick={processExportCSV}
                type="submit"
                className={classes.toolbarButton}
              >
                {GetString('EXPORT_TO_CSV')}
              </Button>
            </GridToolbar>
            <GridColumn
              {...columnProps('Division')}
              title={GetString('COLUMN_TITLE_DIVISION')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('SiteNumber')}
              title={GetString('COLUMN_TITLE_STORE')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('NodeGroup')}
              title={GetString('COLUMN_TITLE_CLUSTER')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('LaneNumber')}
              title={GetString('COLUMN_TITLE_LANE')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('Category')}
              title={GetString('COLUMN_TITLE_LANE_CATEGORY')}
              width="110px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('FailureMessage')}
              title={GetString('COLUMN_TITLE_FAILURE')}
              width="160px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              field="Status"
              title={GetString('COLUMN_TITLE_STATUS')}
              width="215px"
              cells={{
                data: IncidentStatusCell,
                filterCell: IncidentStatusFilterCell,
              }}
            />
            <GridColumn
              field="Actions"
              title={GetString('COLUMN_TITLE_ACTIONS')}
              width="180px"
              filterable={false}
              cells={{ data: TicketActionsCellWrapper }}
            />
            <GridColumn
              field="CreatedOn"
              title={GetString('COLUMN_TITLE_CREATED_ON')}
              width="250px"
              cells={{
                data: CreatedOnDateTimeCell,
                filterCell: dateFilterCell,
              }}
            />
            <GridColumn
              field="EngineerEta"
              title={GetString('COLUMN_TITLE_ETA')}
              width="250px"
              cells={{ data: ETADateTimeCell, filterCell: etaDateFilterCell }}
            />
          </Grid>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {error && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => clearFetchError()}
                >
                  <StringResourceManager stringKey={fetchError.Message} />
                  {`(${status}: ${fetchError.StatusCode})`}
                </Notification>
              )}
              {commandNotificationSuccessMessage && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                  onClose={resetCommandNotificationSuccessMessage}
                >
                  <StringResourceManager
                    stringKey={commandNotificationSuccessMessage}
                  />
                </Notification>
              )}
              {closeTicketNotificationSuccessMessage && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                  onClose={resetCloseTicketNotificationSuccessMessage}
                >
                  <StringResourceManager
                    stringKey={closeTicketNotificationSuccessMessage}
                  />
                </Notification>
              )}
              {commandNotificationRebootSuccessMessage && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                  onClose={resetCommandNotificationSuccessMessage}
                >
                  <StringResourceManager
                    stringKey={commandNotificationRebootSuccessMessage}
                  />
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default TicketsGrid;
